import * as React from 'react';
import AuthButtons from './AuthButtons';
import { Link, Switch, Route, withRouter } from 'react-router-dom';
import { compose, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { RouterProps } from 'react-router';
import { withLocalization, LProps } from '../../utils/wrappers';
import StatIcon, { StatType } from './StatIcon';
import Indicators from '../../shared/Indicator';
import { State } from '../../redux/state';
import a, { bindActionCreators } from '../../redux/actions';
import * as selectors from '../../redux/selectors';
import { Routes, makePathnames } from '../../utils/routerHelper';
import RouteLink from '../../shared/RouteLink';
import Login from './Login';
import Signup from './Signup';
import ForgotPassword from './ForgotPassword';
import PasswordReset from './PasswordReset';
import Overlay from '../../shared/Overlay';
import { TranslationFunction } from 'i18next';
import { CompetitionScore } from '../../userApi';
import { savingsCO2LbsToCars } from '../../utils/userApiHelper';
import Join from './Join';
import CcsfPrelaunchLanding from '../Prelaunch/CcsfPrelaunchLanding';
import UcdEwcPrelaunchLanding from '../Prelaunch/UcdEwcPrelaunchLanding';

interface PageScrollerProps {
  children?: React.ReactNode;
  containerHeight?: string;
  containerWidth?: string;
  animationTimer?: number;
  pageOnChange?: (page: number) => void;
}

// react-page-scroller@2.2.1 uses hooks (React 16.8+). This project uses React 16.6.
const StaticPageScroller: React.SFC<PageScrollerProps> = ({
  children, containerHeight, containerWidth
}) => (
  <div style={{ height: containerHeight, width: containerWidth, overflowY: 'auto', overflowX: 'hidden' }}>
    {children}
  </div>
);

interface PageScrollerComponentProps {
  backgroundImage: string;
  backgroundSet?: string;
  children?: React.ReactNode;
  className?: string;
  alt: string;
}

const PageScrollerComponent = ({
  backgroundImage,
  backgroundSet,
  className,
  children,
  alt
}: PageScrollerComponentProps) => (
  <div className={'page-scroller-component ' + className}>
    <div className="page-scroller-compnent__content">
      <img
        alt={alt}
        className="cc__home_img"
        src={backgroundImage}
        srcSet={backgroundSet}
      />
      {children}
    </div>
  </div>
);

interface ManualProps {
  children?: React.ReactNode;
  readOnly?: boolean;
}

interface Props extends ManualProps {
  landingPagePosition: number;
  competitionLogoUrl: string;
  setLandingPagePosition: a.SetLandingPagePosition;
  canViewCompetitionContent: boolean;
  competitionScore: CompetitionScore;
  hasJoined: boolean;
  hasCompetitionStarted: boolean;
  canBypassCompetitionStart: boolean;
  isLoggedIn: boolean;
  competitionName: string;
  spinner: boolean;
}

const HomeWeb = ({
  t, landingPagePosition, setLandingPagePosition, history, competitionScore, hasJoined, hasCompetitionStarted, canBypassCompetitionStart, isLoggedIn, readOnly, competitionLogoUrl, competitionName, spinner
}: LProps<Props> & RouterProps) => {
  const paths = makePathnames(t);
  const path = history.location.pathname.split('/');
  // Token restores before competitionUser arrives from /me — don't treat that gap as "needs join".
  const sessionHydrating = Boolean(isLoggedIn && !hasJoined && spinner);
  // Keep CCSF on the branded public landing (prelaunch + post-launch language) instead of
  // the legacy campus carousel — including for logged-in users who have not joined yet.
  // Competition admins / division captains bypass so they can use admin tools.
  const showCcsfLanding = !readOnly
    && String(competitionName || '').toLowerCase() === 'ccsf'
    && !hasJoined
    && !sessionHydrating
    && !canBypassCompetitionStart;
  const showUcdEwcLanding = !readOnly
    && String(competitionName || '').toLowerCase() === '2027ewc'
    && !hasJoined
    && !sessionHydrating
    && !canBypassCompetitionStart;
  const authSegment = path[2];
  const homePath = competitionName ? `/${competitionName}` : '/';
  const authTitle = authSegment === 'signup' ? t('Sign up')
    : authSegment === 'join' ? t('Join')
    : authSegment === 'forgot_password' || authSegment === 'password_reset' ? t('Reset password')
    : t('Log in');
  // Show Join for logged-in users who are not yet in this competition (pilot accounts).
  // Must work on the CCSF branded landing — earlier code suppressed Join there.
  // Skip while sessionHydrating so refresh doesn't flash Join/login over Dashboard.
  const showJoinOverlay = Boolean(isLoggedIn && !hasJoined && !sessionHydrating && (authSegment === 'join' || !authSegment));
  const showAuthOverlay = !readOnly && (Boolean(authSegment) || showJoinOverlay);
  const authOverlay = showAuthOverlay && (
    <div className="cc__home__form">
      <Overlay
        title={showJoinOverlay && (!authSegment || authSegment === 'join') ? t('Join') : authTitle}
        labelledBy="cc__auth_dialog_title"
        closeTo={homePath}
        closeLabel={t('Close')}
        onRequestClose={() => history.push(homePath)}
      >
        <h2 id="cc__auth_dialog_title" className="cc__sr_only">
          {showJoinOverlay && (!authSegment || authSegment === 'join') ? t('Join') : authTitle}
        </h2>
        <Switch>
          <Route path={paths[Routes.Login]} component={Login} exact={true} />
          <Route path={paths[Routes.Signup]} component={Signup} exact={true} />
          <Route path={paths[Routes.ForgotPassword]} component={ForgotPassword} exact={true} />
          <Route path={paths[Routes.PasswordReset]} component={PasswordReset} exact={true} />
          <Route path={paths[Routes.Join]} component={Join} exact={true} />
          {showJoinOverlay && <Route component={Join} exact={true} />}
        </Switch>
      </Overlay>
    </div>
  );

  if (showCcsfLanding) {
    return (
      <div id="cc__home" className="cc__home--ccsf-prelaunch">
        <CcsfPrelaunchLanding
          authOverlayOpen={Boolean(authSegment) || showJoinOverlay}
          launched={hasCompetitionStarted}
          isLoggedIn={isLoggedIn}
          hasJoined={hasJoined}
        />
        {authOverlay}
      </div>
    );
  }

  if (showUcdEwcLanding) {
    return (
      <div id="cc__home" className="cc__home--ccsf-prelaunch">
        <UcdEwcPrelaunchLanding
          authOverlayOpen={Boolean(authSegment) || showJoinOverlay}
          launched={hasCompetitionStarted}
          isLoggedIn={isLoggedIn}
          hasJoined={hasJoined}
        />
        {authOverlay}
      </div>
    );
  }

  const slides = sliders(t, competitionScore);
  return (
    <div id="cc__home" className={'cc__columns'}>
      <div
        id="cc__home__fixed_sidebar"
        className={'cc_column'}
      >
        <div id="cc__home__fixed_sidebar__content">
          <Link to="/">
            <img src={competitionLogoUrl} alt={t('Challenge logo')} />
          </Link>
          <AuthButtons readOnly={readOnly} />
          <div id="cc__home__links">
            {!readOnly ?
              <RouteLink route={Routes.About}>{t('About')}</RouteLink>
              :
              <RouteLink route={Routes.GlobalAbout}>{t('About')}</RouteLink>
            }
          </div>
        </div>
      </div>
      <div
        className="cc__home__slides_mobile"
      >
        {slides}
      </div>
      <div
        id="cc__home__carousel"
        className={'cc_column'}
      >
        <div className="cc__home__scroller_indicator">
          <Indicators
            vertical={true}
            length={slides.length}
            active={landingPagePosition}
          />
        </div>
        <StaticPageScroller
          animationTimer={500}
          containerWidth={'100%'}
          containerHeight={'100%'}
          pageOnChange={(oneBasedPosition) => setLandingPagePosition(oneBasedPosition)}
        >
          {slides}
        </StaticPageScroller>
      </div>
      {authOverlay}
    </div>
  );
};

export const scrollToLoginForm = () => {
  setTimeout(() => {
    const cmpnt0 = document.querySelector('.cc__home__page_scroller_cmpnt0');
    if (cmpnt0) {
      cmpnt0.scrollIntoView({ behavior: 'smooth' });
    }
  }, 800);
};

function sliders(t: TranslationFunction, competitionScore: CompetitionScore) {
  const slides = [
    <PageScrollerComponent
      key="0"
      className="cc__page-form cc__home__page_scroller_cmpnt0"
      alt={t('UCSC collage')}
      backgroundImage="/assets/landing/web/ucsc-collage.png"
      backgroundSet="/assets/landing/web/ucsc-collage@2x.png 2x"
    />];
  if (competitionScore) {
    slides.push(
      <PageScrollerComponent
        key="1"
        alt={t('UCSC forest')}
        backgroundImage="/assets/landing/web/ucsc-day-forest.png"
        backgroundSet="/assets/landing/web/ucsc-day-forest@2x.png 2x"

      >
        <div className="cc__stat_icon__container-outer">
          <div className="cc__stat_icon__container-row">
            <StatIcon
              value={competitionScore.savingsCO2Lbs}
              statType={StatType.Carbon}
              statLine={t('lbs CO<sub>2</sub> saved')}
            />
            <StatIcon
              value={savingsCO2LbsToCars(competitionScore.savingsCO2Lbs)}
              statType={StatType.Cars}
              statLine={t('cars removed (equiv)')}
            />
          </div>
          <div className="cc__stat_icon__container-row">
            <StatIcon
              value={competitionScore.participants}
              statType={StatType.Participants}
              statLine={t('participants')}
            />
            <StatIcon
              value={competitionScore.pledgesCompleted}
              statType={StatType.Pledges}
              statLine={t('actions completed')}
            />
          </div>
        </div>
      </PageScrollerComponent>
    );
  }

  return slides.concat([
    <PageScrollerComponent
      key="3"
      alt={t('UCSC courtyard')}
      backgroundImage="/assets/landing/web/ucsc-day-courtyard.png"
      backgroundSet="/assets/landing/web/ucsc-day-courtyard@2x.png 2x"
    >
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '367px', textAlign: 'center', zIndex: 2 }}>
        <div
          style={{ fontSize: '22px', marginBottom: '50px' }}
        >{t('What is the Cool Campus Challenge?')}</div>
        <div
          style={{ fontSize: '42px' }}
        >{t('A friendly competition to reduce UC\'s carbon footprint and create a culture of sustainability across the campuses.')}</div>
      </div>
    </PageScrollerComponent>,
    <PageScrollerComponent
      key="4"
      alt={t('UCSC field')}
      backgroundImage="/assets/landing/web/ucsc-day-field.png"
      backgroundSet="/assets/landing/web/ucsc-day-field@2x.png 2x"
    >
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '288px', marginBottom: '15%', marginTop: '15%', zIndex: 2 }}>
        <div
          style={{ fontSize: '22px', marginBottom: '10px', padding: '10px' }}
        >{t('How does it work?')}</div>
        <div className="home-how_it_works">
          <div className="home-how_it_works-number"><span>1</span></div>
          <div className="home-how_it_works-step">{t('Sign up')}</div>
        </div>
        <div className="home-how_it_works">
          <div className="home-how_it_works-number"><span>2</span></div>
          <div className="home-how_it_works-step">{t('Take sustainable actions')}</div>
        </div>
        <div className="home-how_it_works">
          <div className="home-how_it_works-number"><span>3</span></div>
          <div className="home-how_it_works-step">{t('See your impact')}</div>
        </div>
      </div>
    </PageScrollerComponent>,
    <PageScrollerComponent
      key="5"
      alt={t('UCSC ocean')}
      backgroundImage="/assets/landing/web/ucsc-day-ocean.png"
      backgroundSet="/assets/landing/web/ucsc-day-ocean@2x.png 2x"

    >
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '288px', marginBottom: '25%', marginTop: '25%', zIndex: 2 }}>
        <div style={{ fontSize: '42px', lineHeight: 1.0, marginBottom: '50px' }}>
          <div>{t('Ten campuses.')}</div>
          <div>{t('One goal.')}</div>
          <div>{t('Carbon neutral')}</div>
          <div>{t('by 2025.')}</div>
        </div>
        <div
          style={{ fontSize: '22px' }}
        >{t('Join today and reduce your impact to help create a cleaner, healthier future.')}</div>
      </div>
    </PageScrollerComponent>
  ]);
}

const mapStateToProps = (state: State, _props: Props) => ({
  landingPagePosition: state.landingPagePosition,
  competitionLogoUrl: selectors.competitionLogoUrl(state),
  canViewCompetitionContent: selectors.canViewCompetitionContent(state),
  competitionScore: state.competitionScore,
  hasJoined: selectors.hasJoined(state),
  hasCompetitionStarted: selectors.hasCompetitionStarted(state),
  canBypassCompetitionStart: selectors.canBypassCompetitionStartRequirement(state),
  isLoggedIn: selectors.isLoggedIn(state),
  competitionName: selectors.competitionName(state) || (state.competition && state.competition.name) || '',
  spinner: Boolean(state.spinner)
});

const mapDispatchToProps = (dispatch: Dispatch, _props: Props) =>
  bindActionCreators({
    setLandingPagePosition: a.setLandingPagePostion
  }, dispatch);

export default compose(
  withLocalization('home'),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(HomeWeb) as React.SFC<ManualProps>;
